import { urls } from '@repo/app-constants';
import { Card, Icon, Typography } from '@repo/ui';
import { AgeGroup, formatRetestMonth, getAgeGroup } from '@repo/utils';
import resampleBg from 'assets/images/summary/resample_bg_2.png';
import { Accordion } from 'components';
import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import useCheckoutKit from 'hooks/useCheckoutKit';
import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { usePortalConfigs } from 'hooks/usePortalConfigs';
import { type KeyboardEvent, type MouseEvent, useEffect } from 'react';
import useSubscriptions from 'services/general/subscriptionsList';
import { useRetestInfo } from 'services/mss/useRetestInfo';
import parentStyles from '../ActionPlanCategories.module.css';
import styles from './RetestStep.module.css';

const RETEST_UTM =
  'utm_source=tinyhealth&utm_medium=webapp&utm_campaign=retest-step';
const UPSELL_UTM =
  'utm_source=tinyhealth&utm_medium=webapp&utm_campaign=retest-step-upsell';

// Fallback copy for kits that don't have a computed retest date yet
// (kits_retesting_time is populated by Science, not synchronously with results).
const getRetestWindow = (ageGroup: AgeGroup): string => {
  switch (ageGroup) {
    case AgeGroup.BABY:
      return '4–6 weeks';
    case AgeGroup.TODDLER:
      return '6–8 weeks';
    default:
      return '3 months';
  }
};

interface RetestStepProps {
  kitId: string;
  stepNumber: number;
}

const RetestStep = ({ kitId, stepNumber }: RetestStepProps) => {
  const { currentKit } = useCurrentKitAndTinyAccount();
  const { trackEvent, trackEventOnce } = useAnalytics();
  const { resampleMonth } = useRetestInfo(kitId);
  const { config: portalConfig } = usePortalConfigs(currentKit);
  const { isAnyTinyPlus } = useSubscriptions();
  const { checkoutUrl } = useCheckoutKit({
    kitType: currentKit?.sampling_type,
  });

  const ageGroup = currentKit?.age_at_sampling
    ? getAgeGroup(currentKit.age_at_sampling)
    : AgeGroup.ADULT;

  const retestWindow = resampleMonth
    ? formatRetestMonth(new Date(resampleMonth))
    : getRetestWindow(ageGroup);
  const orderUrl = `${checkoutUrl}&${RETEST_UTM}`;
  const membershipUrl = `${urls.MEMBERSHIP}?${UPSELL_UTM}`;

  const showUpsellBanner = !isAnyTinyPlus && !portalConfig.hide_upsells;

  useEffect(() => {
    trackEventOnce('Retest Step Viewed', `retest-${kitId}`, {
      age_group: ageGroup,
      is_tiny_plus: isAnyTinyPlus,
    });
  }, [kitId]);

  const handleOrderKit = (e: MouseEvent | KeyboardEvent) => {
    if ('key' in e && e.key !== 'Enter' && e.key !== ' ') return;
    trackEvent('Retest Upsell CTA Clicked', {
      cta: 'order_kit',
      age_group: ageGroup,
      href: orderUrl,
    });
    window.open(orderUrl, '_blank');
  };

  const handleUpgrade = () => {
    trackEvent('Retest Upsell CTA Clicked', {
      cta: 'upgrade_tiny_plus',
      age_group: ageGroup,
      href: membershipUrl,
    });
    window.open(membershipUrl, '_blank');
  };

  return (
    <Card variant='wrapper'>
      <div className={parentStyles.card}>
        <Accordion
          header={
            <Typography
              variant='heading-l'
              className={parentStyles.headerTitle}
            >
              {`Step ${stepNumber}: Retest in ${retestWindow}`}
            </Typography>
          }
          initialState={false}
          onToggle={updatedIsOpen => {
            trackEvent('Toggled Accordion', {
              accordion_group: 'action_plan_step',
              accordion_id: `action_plan_step_retest`,
              accordion_title: `Step ${stepNumber}: Retest in ${retestWindow}`,
              state: updatedIsOpen ? 'expanded' : 'collapsed',
              variant: 'v5_action_plan_page',
            });
          }}
        >
          <div className={parentStyles.content}>
            <div className={styles.content}>
              <div
                className={styles.orderKitCard}
                onClick={handleOrderKit}
                role='button'
                tabIndex={0}
                onKeyDown={handleOrderKit}
              >
                <div className={styles.orderKitLeft}>
                  <Icon name='heartHealth' size='m' />
                  <Typography variant='body-m'>Order your next kit</Typography>
                </div>
                <Icon name='chevronForward' size='s' />
              </div>
              {showUpsellBanner && (
                <div className={styles.upsellBanner}>
                  <img
                    src={resampleBg}
                    alt='Tiny Health kit'
                    className={styles.upsellImage}
                  />
                  <div className={styles.upsellText}>
                    <Typography variant='heading-m' color='dark'>
                      Upgrade to Tiny+ and save on retesting
                    </Typography>
                    <Typography variant='body-s' color='grey'>
                      Tiny+ members save $80/kit.
                    </Typography>
                    <button
                      onClick={handleUpgrade}
                      className={styles.upgradeButton}
                    >
                      <Icon
                        name='awardFilled'
                        size='s'
                        className={styles.upgradeIcon}
                      />
                      Upgrade to Tiny+
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Accordion>
      </div>
    </Card>
  );
};

export default RetestStep;
